import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/CartPage.jsx - CORREGIDO SIN BUGS
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Divider,
    Alert,
    CircularProgress,
    Chip,
    Grid
} from '@mui/material';
import {
    ShoppingCart as CartIcon,
    ShoppingBag as CheckoutIcon,
    ArrowBack as BackIcon,
    LocalOffer as CouponIcon
} from '@mui/icons-material';

import { useCart } from '../hooks/useCart';
import { cartHelpers } from '../services/ecommerceApi';
import { useNotification } from '../components/common/GlobalNotifications';
import CartItemLine from '../components/store/CartItemLine';
import CartOrderSummary from '../components/store/CartOrderSummary';

const CartPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();

    const {
        cart,
        loading,
        updateQuantity,
        removeItem,
        applyCoupon,
        removeCoupon,
        cartTotals
    } = useCart();

    const [couponCode, setCouponCode] = useState('');
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [updatingItems, setUpdatingItems] = useState({}); // Track which items are being updated

    const handleIncrementQuantity = async (itemId, currentQuantity) => {
        setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
        try {
            const result = await updateQuantity(itemId, currentQuantity + 1);
            if (!result.success) {
                showError(result.error || t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
            }
        } catch (error) {
            console.error(t('hardcoded.pages_cartpage.error_incrementing_quantity'), error);
            showError(t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleDecrementQuantity = async (itemId, currentQuantity) => {
        if (currentQuantity <= 1) {
            await handleRemoveItem(itemId);
            return;
        }

        setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
        try {
            const result = await updateQuantity(itemId, currentQuantity - 1);
            if (!result.success) {
                showError(result.error || t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
            }
        } catch (error) {
            console.error(t('hardcoded.pages_cartpage.error_decrementing_quantity'), error);
            showError(t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleRemoveItem = async (itemId) => {
        setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
        try {
            const result = await removeItem(itemId);
            if (result.success) {
                showSuccess(t('cart.remove'));
            } else {
                showError(result.error || t('hardcoded.components_store_cartdrawer.error_al_eliminar_producto'));
            }
        } catch (error) {
            console.error(t('hardcoded.pages_cartpage.error_removing_item'), error);
            showError(t('hardcoded.components_store_cartdrawer.error_al_eliminar_producto'));
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleSetQuantity = async (itemId, quantity) => {
        if (!Number.isFinite(quantity) || quantity <= 0) {
            await handleRemoveItem(itemId);
            return;
        }

        setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
        try {
            const result = await updateQuantity(itemId, quantity);
            if (!result.success) {
                showError(result.error || t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
            }
        } catch (error) {
            console.error(t('hardcoded.pages_cartpage.error_setting_quantity'), error);
            showError(t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad'));
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        setApplyingCoupon(true);
        try {
            await applyCoupon(couponCode.trim().toUpperCase());
            showSuccess(t('hardcoded.components_store_cartdrawer.cupon_aplicado_correctamente'));
            setCouponCode('');
        } catch (error) {
            // Error ya mostrado por useCart
            console.error(t('hardcoded.pages_cartpage.error_applying_coupon'), error);
        } finally {
            setApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = async () => {
        try {
            await removeCoupon();
            showSuccess(t('hardcoded.components_store_cartdrawer.cupon_removido'));
        } catch (error) {
            console.error(t('hardcoded.hooks_usecart.error_removing_coupon'), error);
        }
    };

    const handleCheckout = () => {
        navigate('/checkout');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const isEmpty = !cart || !cart.items || cart.items.length === 0;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/store')}
                    sx={{ mb: 2 }}
                >
                    {i18n.t('hardcoded.components_store_cartdrawer.continuar_comprando')}</Button>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CartIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" fontWeight={700}>
                                {t('hardcoded.components_store_cartdrawer.carrito_de_compras')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {isEmpty ? t('hardcoded.components_store_cartdrawer.tu_carrito_esta_vacio') : t('hardcoded.pages_cartpage.carttotalsitemc_productos', { cartTotalsitemC: cartTotals?.itemCount || 0 })}
                            </Typography>
                        </Box>
                    </Box>

                </Box>
            </Box>

            {isEmpty ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <CartIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h5" gutterBottom color="text.secondary">
                        {t('hardcoded.components_store_cartdrawer.tu_carrito_esta_vacio')}</Typography>
                    <Typography variant="body1" color="text.secondary" mb={3}>
                        {t('hardcoded.components_store_cartdrawer.agrega_algunos_productos_para_empezar')}</Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/store')}
                    >
                        {t('hardcoded.pages_cartpage.ir_a_la_tienda')}</Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {/* Cart Items */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 2 }}>
                            {cart.items.map((item) => {
                                const isUpdating = updatingItems[item.id];
                                return (
                                    <Box key={item.id} sx={{ opacity: isUpdating ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                                        <CartItemLine
                                            item={item}
                                            editable
                                            disabled={isUpdating}
                                            onDecrease={() => handleDecrementQuantity(item.id, item.quantity)}
                                            onIncrease={() => handleIncrementQuantity(item.id, item.quantity)}
                                            onSetQuantity={(qty) => handleSetQuantity(item.id, qty)}
                                            onRemove={() => handleRemoveItem(item.id)}
                                        />
                                        {isUpdating && <CircularProgress size={18} sx={{ mb: 1 }} />}
                                    </Box>
                                );
                            })}
                        </Paper>
                    </Grid>

                    {/* Order Summary */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {t('hardcoded.pages_cartpage.resumen_del_pedido')}</Typography>

                            <Divider sx={{ my: 2 }} />

                            {/* Coupon Section */}
                            {cartTotals?.couponCode ? (
                                <Box sx={{ mb: 2 }}>
                                    <Chip
                                        label={t('hardcoded.components_store_cartdrawer.cupon_couponcode', { couponCode: cartTotals.couponCode })}
                                        color={cartTotals?.couponState?.status === 'invalid' ? 'warning' : 'success'}
                                        icon={<CouponIcon />}
                                        onDelete={handleRemoveCoupon}
                                        sx={{ mb: 2 }}
                                    />
                                    {cartTotals?.couponState?.status === 'invalid' && (
                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                            {cartTotals?.couponState?.message || t('hardcoded.hooks_usecart.cupon_invalido')}
                                        </Alert>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('hardcoded.pages_cartpage.tienes_un_cupon')}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder={t('hardcoded.pages_adminstorepage.codigo')}
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleApplyCoupon();
                                                }
                                            }}
                                            disabled={applyingCoupon}
                                            sx={{ flex: 1 }}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={handleApplyCoupon}
                                            disabled={!couponCode.trim() || applyingCoupon}
                                        >
                                            {applyingCoupon ? t('hardcoded.components_store_cartdrawer.aplicando') : t('hardcoded.components_store_cartdrawer.aplicar')}
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <CartOrderSummary
                                cartTotals={cartTotals}
                                totalSavings={cartHelpers.calculateSavings(cart)}
                                showSavings
                            />

                            {cartTotals && !cartTotals.hasFreeShipping && cartTotals.subtotal < 100 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        Agrega {cartHelpers.formatCurrency(100 - cartTotals.subtotal)} {t('hardcoded.components_store_cartdrawer.mas_para_envio_gratuito')}</Typography>
                                </Alert>
                            )}

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<CheckoutIcon />}
                                onClick={handleCheckout}
                                sx={{ py: 1.5, fontWeight: 600 }}
                            >
                                {t('hardcoded.components_store_cartdrawer.proceder_al_checkout')}</Button>

                            <Button
                                fullWidth
                                variant="text"
                                onClick={() => navigate('/store')}
                                sx={{ mt: 1 }}
                            >
                                {i18n.t('hardcoded.components_store_cartdrawer.continuar_comprando')}</Button>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

export default CartPage;